import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Volume2, VolumeX, ExternalLink, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { sendReelEvent } from '../utils/reelEvents';

export default function ReelPlayer({ reel, isActive, onNext, onPrev }) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [showCaptions, setShowCaptions] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [watchStartTime, setWatchStartTime] = useState(null);
  
  const progressTracked = useRef({ 
    q25: false, 
    q50: false, 
    q75: false, 
    q95: false, 
    complete: false 
  });
  const impressionSent = useRef(false);
  const longPressTimer = useRef(null);
  const [isPaused, setIsPaused] = useState(false);

  // Resolve video URL - prefer asset_id over direct URL
  const getVideoUrl = useCallback(() => {
    if (reel.video_asset_id) {
      // Resolve asset_id to /api/mock/assets/{id}
      return `${process.env.REACT_APP_BACKEND_URL}/api/mock/assets/${reel.video_asset_id}`;
    }
    return reel.video_url;
  }, [reel.video_asset_id, reel.video_url]);

  // Resolve poster URL - prefer asset_id over direct URL
  const getPosterUrl = useCallback(() => {
    if (reel.poster_asset_id) {
      return `${process.env.REACT_APP_BACKEND_URL}/api/mock/assets/${reel.poster_asset_id}`;
    }
    return reel.poster_url;
  }, [reel.poster_asset_id, reel.poster_url]);

  // Get device and network info
  const getDeviceInfo = useCallback(() => {
    const ua = navigator.userAgent;
    let device = 'desktop';
    if (/iPhone|iPad|iPod/.test(ua)) device = 'ios';
    else if (/Android/.test(ua)) device = 'android';
    else if (/Mobile/.test(ua)) device = 'mobile';
    
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const network = connection ? connection.effectiveType : 'unknown';
    
    return { device, network };
  }, []);

  // Send impression when visible for 1s with 50% visibility
  useEffect(() => {
    if (!isActive || impressionSent.current) return;
    
    const timer = setTimeout(() => {
      const { device, network } = getDeviceInfo();
      sendReelEvent(reel.id, 'reel_impression', { device, network });
      impressionSent.current = true;
    }, 1000);

    return () => clearTimeout(timer);
  }, [isActive, reel.id, getDeviceInfo]);

  // Auto-play when active
  useEffect(() => {
    if (isActive && videoRef.current) {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
            setWatchStartTime(Date.now());
            const { device, network } = getDeviceInfo();
            sendReelEvent(reel.id, 'reel_start', { device, network });
          })
          .catch((err) => {
            console.log('Auto-play blocked:', err);
            setIsPlaying(false);
          });
      }
    } else if (!isActive && videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [isActive, reel.id, getDeviceInfo]);

  const handlePlayPause = useCallback(() => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
      sendReelEvent(reel.id, 'reel_pause', { 
        progress: Math.round(progress),
        watch_ms: watchStartTime ? Date.now() - watchStartTime : 0
      });
    } else {
      videoRef.current.play();
      setIsPlaying(true);
      if (videoRef.current.currentTime === 0) {
        setWatchStartTime(Date.now());
        const { device, network } = getDeviceInfo();
        sendReelEvent(reel.id, 'reel_start', { device, network });
      } else {
        sendReelEvent(reel.id, 'reel_resume', { 
          progress: Math.round(progress),
          watch_ms: watchStartTime ? Date.now() - watchStartTime : 0
        });
      }
    }
  }, [isPlaying, reel.id, progress, watchStartTime, getDeviceInfo]);

  const handleTimeUpdate = useCallback(() => {
    if (!videoRef.current) return;

    const percent = (videoRef.current.currentTime / videoRef.current.duration) * 100;
    setProgress(percent);

    // Track quartile progress
    if (percent >= 25 && !progressTracked.current.q25) {
      sendReelEvent(reel.id, 'reel_q25', { 
        watch_ms: watchStartTime ? Date.now() - watchStartTime : 0 
      });
      progressTracked.current.q25 = true;
    }
    if (percent >= 50 && !progressTracked.current.q50) {
      sendReelEvent(reel.id, 'reel_q50', { 
        watch_ms: watchStartTime ? Date.now() - watchStartTime : 0 
      });
      progressTracked.current.q50 = true;
    }
    if (percent >= 75 && !progressTracked.current.q75) {
      sendReelEvent(reel.id, 'reel_q75', { 
        watch_ms: watchStartTime ? Date.now() - watchStartTime : 0 
      });
      progressTracked.current.q75 = true;
    }
    if (percent >= 95 && !progressTracked.current.q95) {
      sendReelEvent(reel.id, 'reel_q95', { 
        watch_ms: watchStartTime ? Date.now() - watchStartTime : 0 
      });
      progressTracked.current.q95 = true;
      
      // Auto-mark as complete at 95%
      sendReelEvent(reel.id, 'reel_complete', { 
        watch_ms: watchStartTime ? Date.now() - watchStartTime : 0,
        duration_ms: videoRef.current.duration * 1000
      });
    }
  }, [reel.id, watchStartTime]);

  const toggleMute = useCallback((e) => {
    e.stopPropagation();
    if (videoRef.current) {
      const newMuted = !isMuted;
      videoRef.current.muted = newMuted;
      setIsMuted(newMuted);
      sendReelEvent(reel.id, newMuted ? 'reel_mute' : 'reel_unmute');
    }
  }, [isMuted, reel.id]);

  const handleCTAClick = useCallback((e) => {
    e.stopPropagation();
    const { device, network } = getDeviceInfo();
    sendReelEvent(reel.id, 'reel_cta_click', { 
      url: reel.cta?.url,
      label: reel.cta?.label,
      course_id: reel.course_id,
      device,
      network,
      watch_ms: watchStartTime ? Date.now() - watchStartTime : 0
    });
    
    if (reel.cta?.url) {
      window.open(reel.cta.url, '_blank', 'noopener,noreferrer');
    }
  }, [reel, getDeviceInfo, watchStartTime]);

  // Long-press for pause/resume
  const handleTouchStart = useCallback((e) => {
    longPressTimer.current = setTimeout(() => {
      if (isPlaying) {
        videoRef.current?.pause();
        setIsPaused(true);
        sendReelEvent(reel.id, 'reel_pause', { 
          method: 'long_press',
          watch_ms: watchStartTime ? Date.now() - watchStartTime : 0
        });
      }
    }, 500);
  }, [isPlaying, reel.id, watchStartTime]);

  const handleTouchEnd = useCallback((e) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
    
    if (isPaused) {
      videoRef.current?.play();
      setIsPaused(false);
      sendReelEvent(reel.id, 'reel_resume', { 
        method: 'long_press',
        watch_ms: watchStartTime ? Date.now() - watchStartTime : 0
      });
    } else {
      handlePlayPause();
    }
  }, [isPaused, reel.id, watchStartTime, handlePlayPause]);

  const handleError = useCallback(() => {
    setHasError(true);
    setIsPlaying(false);
  }, []);

  const handleRetry = useCallback(() => {
    setHasError(false);
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, []);

  // Error state
  if (hasError) {
    return (
      <div className="relative w-full h-full bg-black flex flex-col items-center justify-center">
        {reel.poster_url && (
          <img 
            src={reel.poster_url} 
            alt={reel.title}
            className="absolute inset-0 w-full h-full object-cover opacity-50"
          />
        )}
        <div className="relative z-10 text-center p-8">
          <AlertCircle className="w-16 h-16 text-white mb-4 mx-auto" />
          <p className="text-white text-lg mb-4">Video kon niet laden</p>
          <Button 
            onClick={handleRetry}
            className="bg-white text-black hover:bg-gray-200"
          >
            Tap om opnieuw te proberen
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="relative w-full h-full bg-black" 
      data-reel-id={reel.id}
      style={{ 
        aspectRatio: '9/16',
        maxWidth: '100vw',
        maxHeight: '100vh'
      }}
    >
      {/* Video */}
      <video
        ref={videoRef}
        src={getVideoUrl()}
        poster={getPosterUrl()}
        className="w-full h-full object-contain"
        playsInline
        muted={isMuted}
        controls={false}
        preload="none"
        loop
        onTimeUpdate={handleTimeUpdate}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onError={handleError}
        onEnded={() => {
          if (onNext) {
            sendReelEvent(reel.id, 'reel_swipe_next', { 
              method: 'auto_advance',
              watch_ms: watchStartTime ? Date.now() - watchStartTime : 0
            });
            onNext();
          }
        }}
        crossOrigin="anonymous"
      >
        {reel.captions_url && showCaptions && (
          <track kind="captions" src={reel.captions_url} srcLang="en" label="English" />
        )}
      </video>

      {/* Tap area for play/pause (with long-press support) */}
      <div
        className="absolute inset-0"
        onClick={handlePlayPause}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{ zIndex: 1 }}
      />

      {/* Progress bar - safe area top */}
      <div 
        className="absolute left-0 right-0 h-1 bg-white/30" 
        style={{ 
          top: 'env(safe-area-inset-top, 0px)',
          zIndex: 10 
        }}
      >
        <div
          className="h-full bg-white transition-all duration-100"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Mute toggle - top right with safe area */}
      <div 
        className="absolute flex gap-2 items-center" 
        style={{ 
          top: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
          right: '1rem',
          zIndex: 10 
        }}
      >
        <Button
          size="icon"
          variant="ghost"
          className="rounded-full bg-black/40 text-white hover:bg-black/60 backdrop-blur-sm"
          onClick={toggleMute}
          aria-label={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
        </Button>
      </div>

      {/* Info overlay - bottom with safe area */}
      <div 
        className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent"
        style={{ 
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
          paddingTop: '4rem',
          paddingLeft: '1rem',
          paddingRight: '1rem',
          zIndex: 10 
        }}
      >
        <h3 className="text-white text-xl font-bold mb-2">{reel.title}</h3>
        
        {reel.description && (
          <p className="text-white/90 text-sm mb-2 line-clamp-2">{reel.description}</p>
        )}
        
        {reel.tags && reel.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {reel.tags.map((tag, idx) => (
              <span key={idx} className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs">
                #{tag}
              </span>
            ))}
          </div>
        )}
        
        {reel.roles && reel.roles.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {reel.roles.map((role, idx) => (
              <span key={idx} className="px-2 py-1 bg-blue-500/30 backdrop-blur-sm rounded-full text-white text-xs capitalize">
                {role}
              </span>
            ))}
          </div>
        )}

        {/* CTA Button */}
        {reel.cta && (
          <Button
            onClick={handleCTAClick}
            className="w-full sm:w-auto bg-white text-black hover:bg-gray-200 font-semibold mt-2"
            size="lg"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            {reel.cta.label}
          </Button>
        )}

        {/* Captions toggle */}
        {reel.captions_url && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowCaptions(!showCaptions);
            }}
            className="mt-3 text-white/80 text-sm hover:text-white"
            aria-label="Toggle captions"
          >
            {showCaptions ? '🔊 CC On' : '🔇 CC Off'}
          </button>
        )}
      </div>

      {/* Pause indicator */}
      {isPaused && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
          <div className="text-white text-6xl">⏸️</div>
        </div>
      )}
    </div>
  );
}
